import DemographicsForm, {
  DemographicsFormFields,
} from "@/components/pages/user-info/demographics-form-controlled";
import {
  DemographicsFormSkeleton,
  LanguageLearningObjectivesFormSkeleton,
  UserProfileSkeleton,
} from "@/components/pages/user-info/user-profile-skeleton";
import { FormProvider, useForm } from "react-hook-form";
import LanguageLearningObjectivesForm, {
  LanguageLearningObjectivesFormFields,
} from "@/components/pages/user-info/language-objectives-form-controlled";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { CardHeader } from "@/components/ui/card";
import { CardWrapper } from "@/components/card-wrapper";
import { ErrorFetchingData } from "@/components/common/user-messages";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { UpdateUserProfile } from "@/client/core";
import { toast } from "sonner";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";
import { useUserInformation } from "@/hooks/api/useUserProfileApi";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const ProfileFormFields = z.object({
  ...DemographicsFormFields,
  ...LanguageLearningObjectivesFormFields,
});

export type UserProfileFormValues = z.infer<typeof ProfileFormFields>;

export default function ProfilePage() {
  const { t } = useTranslation(["pages", "common"]);
  const { token, user } = usePocketBase();
  const [activeTab, setActiveTab] = useState("demographics");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFieldChanged, setIsFieldChanged] = useState(false);
  const [userProfileFormDefaults, setUserProfileFormDefaults] = useState<
    UserProfileFormValues | undefined
  >(undefined);

  const {
    data: userInformationData,
    isLoading,
    error,
    refetch,
  } = useUserInformation();

  const userProfileForm = useForm<UserProfileFormValues>({
    resolver: zodResolver(ProfileFormFields),
    defaultValues: {
      // Demographics fields - provide empty string defaults
      firstName: "",
      lastName: "",
      gender: undefined,
      ageRange: undefined,
      country: "",
      nativeLanguage: "",
      // Language learning objectives - provide empty array
      learnLanguages: [],
    },
    mode: "onChange",
  });

  // Initialize form with fetched user information
  useEffect(() => {
    console.log("User Information Data:", userInformationData);
    if (userInformationData) {
      userProfileForm.reset(userInformationData);
      setUserProfileFormDefaults(userInformationData);
    }
  }, [userInformationData, userProfileForm]);

  // Detect changes in form fields
  useEffect(() => {
    const subscription = userProfileForm.watch((value, { name, type }) => {
      if (
        userProfileFormDefaults &&
        JSON.stringify(userProfileFormDefaults) !==
          JSON.stringify(userProfileForm.getValues())
      ) {
        setIsFieldChanged(true);
      } else {
        setIsFieldChanged(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [userProfileForm.watch, userProfileFormDefaults]);

  const onSubmit = async (values: UserProfileFormValues) => {
    setIsSubmitting(true);
    setIsFieldChanged(false);

    const response = await UpdateUserProfile(values, user?.id, token);
    if (response !== "success") {
      toast.error(`Failed to update demographics: ${response}`);
      setIsSubmitting(false);
      return;
    } else {
      toast.success(t("pages:userProfile.updateSuccessMessage"));
    }
    setIsSubmitting(false);
    await refetch();
  };

  // return <UserProfileSkeleton />;

  if (error) {
    return <ErrorFetchingData />;
  }

  // if (isLoading || !userInformationData) {
  //   return <UserProfileSkeleton />;
  // }

  function RenderAccountInfo() {
    return (
      <div className="flex flex-row items-center justify-start flex-wrap gap-4 mb-4">
        <div>
          <span>{user?.email}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-background p-10 lg:p-12">
      <div className="flex flex-row mx-auto space-y-2 justify-end justify-between gap-4">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="align-start text-3xl font-bold tracking-tight">
              {t("pages:userProfile.title")}
            </h1>
            <p className="hidden sm:inline text-muted-foreground">
              {t("pages:userProfile.subtitle")}
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center">
          <Button
            type="submit"
            disabled={!isFieldChanged || isSubmitting}
            form="userProfileForm"
            className="min-w-28 md:min-w-40 mt-2"
          >
            {isSubmitting
              ? t("pages:userProfile.updatingButton")
              : t("common:buttons.update")}
          </Button>
        </div>
      </div>

      <FormProvider {...userProfileForm}>
        <form
          id="userProfileForm"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={userProfileForm.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          {/* Small screen, use tabs */}
          <div className="md:hidden">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <ScrollArea className="overflow-auto max-w-11/12 pb-2">
                <TabsList className="grid w-full grid-cols-2 mt-4 min-w-max">
                  <TabsTrigger
                    value="demographics"
                    className="flex items-center gap-2"
                  >
                    {t("pages:demographics.userInfoTitle")}
                  </TabsTrigger>
                  <TabsTrigger
                    value="languageLearningObjectives"
                    className="flex items-center gap-2"
                  >
                    {t(
                      "pages:learningLanguageObjectives.languageLearningTitle",
                    )}
                  </TabsTrigger>
                </TabsList>
              </ScrollArea>
              <TabsContent value="demographics" className="mt-4">
                <CardWrapper className="w-full px-6 md:px-10">
                  {isLoading ? (
                    <DemographicsFormSkeleton />
                  ) : (
                    <DemographicsForm />
                  )}
                </CardWrapper>
              </TabsContent>
              <TabsContent value="languageLearningObjectives" className="mt-4">
                <CardWrapper className="w-full px-6 md:px-10">
                  {isLoading ? (
                    <LanguageLearningObjectivesFormSkeleton />
                  ) : (
                    <LanguageLearningObjectivesForm
                      mode="minimal"
                      isNoDelete
                      size="lg"
                    />
                  )}
                </CardWrapper>
              </TabsContent>
            </Tabs>
          </div>

          {/* Large screen, show forms side by side */}
          <div className="hidden md:flex flex-col lg:flex-row gap-4 items-start">
            <CardWrapper className="w-full px-6 md:px-10">
              <CardHeader className="pb-0">
                <h2 className="text-lg font-medium">
                  {t("pages:demographics.userInfoTitle")}
                </h2>
                <RenderAccountInfo />
              </CardHeader>
              {isLoading ? <DemographicsFormSkeleton /> : <DemographicsForm />}
            </CardWrapper>
            <CardWrapper className="w-full px-2 md:px-6">
              <CardHeader className="pb-0">
                <h2 className="text-lg font-medium">
                  {t("pages:learningLanguageObjectives.languageLearningTitle")}
                </h2>
              </CardHeader>
              {isLoading ? (
                <LanguageLearningObjectivesFormSkeleton />
              ) : (
                <LanguageLearningObjectivesForm
                  mode="minimal"
                  isNoDelete
                  size="lg"
                />
              )}
            </CardWrapper>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
